import { cn } from 'lib/utils'
import { User } from 'lucide-react'
import { Avatar, AvatarFallback } from './Avatar'

interface AvatarFallbackWithInitialsProps {
  name: string
  id: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

const idToColor = (id: string): { primary: string; secondary: string } => {
  // Simple hash function
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }

  // Generate primary color
  const h = hash % 360 // Hue: 0-359
  const s = 70 + (hash % 10) // Saturation
  const l = 70 + (hash % 10) // Lightness

  const secondaryH = (h + 60) % 360

  return {
    primary: `hsl(${h}, ${s}%, ${l}%)`,
    secondary: `hsl(${secondaryH}, ${s}%, ${l}%)`,
  }
}

const getInitials = (name: string): string | null => {
  if (!name.trim()) {
    return null
  }

  // filter out non-alphabetic characters
  const words = name
    .trim()
    .split(/\s+/)
    .filter(word => /^[a-zA-Z]+$/.test(word))

  if (words.length > 1) {
    // Take first letter from first two words
    return words
      .slice(0, 2)
      .map(word => word[0].toUpperCase())
      .join('')
  }

  // For single word, take first two letters
  return name.slice(0, 2).toUpperCase()
}

const getSizeClasses = (size: AvatarFallbackWithInitialsProps['size'] = 'sm') => {
  switch (size) {
    case 'sm':
      return 'text-xs'
    case 'lg':
      return 'text-4xl'
    default:
      return 'text-sm'
  }
}

const getBorderWidth = (size: AvatarFallbackWithInitialsProps['size'] = 'sm') => {
  switch (size) {
    case 'lg':
      return '3px'
    default:
      return '2px'
  }
}

export const AvatarFallbackWithInitials = ({
  name,
  id,
  className,
  size = 'sm',
}: AvatarFallbackWithInitialsProps) => {
  const { primary, secondary } = idToColor(id)
  const initials = getInitials(name)
  const sizeClasses = getSizeClasses(size)
  const borderWidth = getBorderWidth(size)

  return (
    <Avatar className={cn(className, 'h-full w-full')}>
      <AvatarFallback
        className={cn('text-center leading-[1.4] font-medium not-italic', sizeClasses, className)}
        style={{
          background: `linear-gradient(white, white) padding-box,
                      linear-gradient(45deg, ${primary}, ${secondary}) border-box`,
          border: `${borderWidth} solid transparent`,
          borderRadius: '100%',
        }}
      >
        {initials || <User size={32} className="text-gray-500" />}
      </AvatarFallback>
    </Avatar>
  )
}
